import { CombinedGraphQLErrors, LinkError } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { sharedMessages, useIntl } from '@faceup/localization'
import { useNotification } from '@faceup/ui-base'
import { chunker, MAX_UPLOAD_FILES_PER_REQUEST, toPresignedPostFormData } from '@faceup/utils'
import { captureException } from '@sentry/react'
import { useMemo, useState } from 'react'
import { graphql } from '../__generated__'

const mutations = {
  CreateAttachmentUploads: graphql(`
    mutation CreateAttachmentUploadsMutation($input: CreateAttachmentUploadsInput!) {
      createAttachmentUploads(input: $input) {
        uploads {
          tempKey
          url
          fields
        }
      }
    }
  `),
}

export class FileUploadError extends Error {}

export type UploadedFile = {
  tempKey: string
  filename: string
  mimetype: string
}

export const useUploadFiles = () => {
  const [createUploads] = useMutation(mutations.CreateAttachmentUploads)
  const [pendingUploads, setPendingUploads] = useState(0)
  const notification = useNotification()
  const { formatMessage } = useIntl()

  const uploaders = useMemo(() => {
    const uploadChunk = async (files: File[]) => {
      const { data } = await createUploads({ variables: { input: { count: files.length } } })
      const uploads = data?.createAttachmentUploads?.uploads ?? []

      return Promise.all(
        files.map(async (file, index) => {
          const upload = uploads[index]
          if (!upload) {
            throw new Error('Missing upload URL for file')
          }

          const response = await fetch(upload.url, {
            method: 'POST',
            body: toPresignedPostFormData(upload.fields, file),
          })
          if (!response.ok) {
            throw new Error(`File upload failed: ${response.status}`)
          }

          return {
            tempKey: upload.tempKey,
            filename: file.name,
            mimetype: file.type || 'application/octet-stream',
          }
        })
      )
    }

    const uploadFiles = async (files: File[]): Promise<UploadedFile[]> => {
      if (files.length === 0) {
        return []
      }

      setPendingUploads(count => count + 1)
      try {
        const uploadedChunks = await Promise.all(
          chunker(files, MAX_UPLOAD_FILES_PER_REQUEST).map(uploadChunk)
        )
        return uploadedChunks.flat()
      } catch (error) {
        if (CombinedGraphQLErrors.is(error) || LinkError.is(error)) {
          throw error
        }
        captureException(error, { tags: { type: 'file_upload' } })
        notification.error({ message: formatMessage(sharedMessages.apiError) })
        throw new FileUploadError('File upload failed', { cause: error })
      } finally {
        setPendingUploads(count => count - 1)
      }
    }

    const uploadFile = async (file: File) => {
      const [uploaded] = await uploadFiles([file])
      if (!uploaded) {
        throw new FileUploadError('File upload returned nothing')
      }
      return uploaded
    }

    const uploadFileGroups = async (groups: File[][]) => {
      const uploaded = await uploadFiles(groups.flat())
      const offsets = groups.map((_group, index) =>
        groups.slice(0, index).reduce((sum, group) => sum + group.length, 0)
      )
      return groups.map((group, index) =>
        uploaded.slice(offsets[index], (offsets[index] ?? 0) + group.length)
      )
    }

    const uploadAnswerFiles = async <Answer extends { files?: File[] | null }>(
      answers: Answer[]
    ): Promise<(Omit<Answer, 'files'> & { files?: UploadedFile[] })[]> => {
      const uploaded = await uploadFileGroups(answers.map(answer => answer.files ?? []))
      return answers.map(({ files, ...answer }, index) => ({
        ...answer,
        ...(files && { files: uploaded[index] ?? [] }),
      }))
    }

    return { uploadFiles, uploadFile, uploadFileGroups, uploadAnswerFiles }
  }, [createUploads, notification, formatMessage])

  const { uploadFiles, ...otherUploaders } = uploaders
  return [uploadFiles, { loading: pendingUploads > 0, ...otherUploaders }] as const
}
